import type { Key } from 'react-aria-components';

import type { FlatRow } from '../flattenRows';

export type RowBodyItem<T> = {
  type: 'row';
  id: Key;
  flatRow: FlatRow<T>;
  isDetailExpanded: boolean;
  isLoadMoreTarget: boolean;
  isActive: boolean;
};

export type DetailBodyItem<T> = { type: 'detail'; id: Key; row: T; parentId: Key };

const sameLines = (left: boolean[], right: boolean[]) =>
  left === right || (left.length === right.length && left.every((line, index) => line === right[index]));

const sameFlatRow = <T>(left: FlatRow<T>, right: FlatRow<T>) =>
  left.id === right.id &&
  left.level === right.level &&
  left.hasChildren === right.hasChildren &&
  left.isExpanded === right.isExpanded &&
  left.isLastChild === right.isLastChild &&
  sameLines(left.ancestorLines, right.ancestorLines);

export class BodyItemCache<T extends object> {
  private readonly rows = new WeakMap<T, RowBodyItem<T>>();
  private readonly details = new WeakMap<T, DetailBodyItem<T>>();

  row(flatRow: FlatRow<T>, isDetailExpanded: boolean, isLoadMoreTarget: boolean, isActive: boolean): RowBodyItem<T> {
    const cached = this.rows.get(flatRow.row);
    if (
      cached &&
      cached.isDetailExpanded === isDetailExpanded &&
      cached.isLoadMoreTarget === isLoadMoreTarget &&
      cached.isActive === isActive &&
      sameFlatRow(cached.flatRow, flatRow)
    ) {
      return cached;
    }
    const item: RowBodyItem<T> = { type: 'row', id: flatRow.id, flatRow, isDetailExpanded, isLoadMoreTarget, isActive };
    this.rows.set(flatRow.row, item);
    return item;
  }

  detail(row: T, parentId: Key): DetailBodyItem<T> {
    const cached = this.details.get(row);
    if (cached && cached.parentId === parentId) {
      return cached;
    }
    const item: DetailBodyItem<T> = { type: 'detail', id: `${String(parentId)}:detail`, row, parentId };
    this.details.set(row, item);
    return item;
  }
}
